import {InstallationStatus} from '@hconnect/common/types'
import {Autocomplete as MuiAutocomplete, TextField} from '@mui/material'
import {useCallback, useMemo, FC, SyntheticEvent} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {Item} from '../types'

type InstallationStatusFieldProps = {
  item: Item
  index: number
  onChange: (
    index: number,
    installationStatus: InstallationStatus | undefined,
    installationStatusText: string | undefined
  ) => void
}

export const InstallationStatusField: FC<InstallationStatusFieldProps> = ({
  item,
  index,
  onChange
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const installationStatusOptions = useMemo(
    () =>
      Object.values(InstallationStatus)
        .filter((status) => status !== InstallationStatus.FreeText)
        .map((installationStatus) =>
          t(`responsiblePersonDialog.installationStatus.${installationStatus}`)
        ),
    [t]
  )

  const installationStatusReverseMap = useMemo(() => {
    const map = new Map<string, InstallationStatus>()
    Object.values(InstallationStatus).forEach((status) => {
      if (status === InstallationStatus.FreeText) return
      const translated = t(`responsiblePersonDialog.installationStatus.${status}`)
      map.set(translated, status)
    })
    return map
  }, [t])

  const displayValue = useMemo((): string => {
    if (!item.installationStatus) return ''
    if (item.installationStatus === InstallationStatus.FreeText) {
      return item.installationStatusText || ''
    }
    const enumValue = Object.values(InstallationStatus).find((v) => v === item.installationStatus)
    if (enumValue && enumValue !== InstallationStatus.FreeText) {
      return t(`responsiblePersonDialog.installationStatus.${enumValue}`)
    }
    return item.installationStatus
  }, [item.installationStatus, item.installationStatusText, t])

  const handleInputChange = useCallback(
    (_: SyntheticEvent, newValue: string) => {
      const enumValue = installationStatusReverseMap.get(newValue)
      if (enumValue) {
        onChange(index, enumValue, undefined)
      } else if (!newValue) {
        onChange(index, undefined, undefined)
      } else {
        onChange(index, InstallationStatus.FreeText, newValue)
      }
    },
    [index, installationStatusReverseMap, onChange]
  )

  return (
    <MuiAutocomplete
      value={displayValue || null}
      options={installationStatusOptions}
      freeSolo
      data-test-id={`item-installation-status-${index}`}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="filled"
          fullWidth
          label={t('responsiblePersonDialog.label.installationStatus')}
        />
      )}
    />
  )
}
